import React, { ReactElement, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import InputLabel from './InputLabel';
import { getBorders, getStatusColor, getInputTheme } from '../style/';
import StatusIcon from '../atoms/icons/StatusIcon';
import { isDarkMode } from '../utils/helpers';
import { TextAreaV2 } from 'swui';
import { ITextAreaProps } from './types/TextArea';
import { FONT_FAMILIES } from 'theme/material/constants';

const Container = styled('div')({
  paddingBottom: 24,
});

const StyledTextArea = styled(TextAreaV2)(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    width: '100%',
    padding: '5px 10px !important',
    borderRadius: '3px !important',
    boxSizing: 'border-box !important',
    background: `${getInputTheme().backgroundInput} !important`,
    color: `${getInputTheme().colorInput} !important`,
    fontFamily: `${FONT_FAMILIES.primary} !important`,
    overflow: 'auto !important',
    boxShadow: '1px 1px 3px 0px rgba(0, 0, 0, 0.2) inset',
    '&:focus': {
      outline: 'none',
      border: getBorders().FOCUS,
      background: getInputTheme().backgroundInput,
      color: `${getInputTheme().colorInput} !important`,
    },
    '&::placeholder': {
      color: '#C9C9C9 !important',
      fontWeight: 400,
      lineHeight: '23px !important',
      letterSpacing: '-0.32px !important',
    },
  }),
  ({ resize }) => ({
    resize: `${resize} !important`,
  })
);

const TextAreaContainer = styled('div')`
  position: relative;
`;

const StatusMessage = styled('div')(({ status }: { status: string }) => ({
  color: getStatusColor()[status],
}));

const Counter = styled('div')({
  color: 'rgb(204, 84, 84)',
});

const MessageContainer = styled('div')({
  position: 'absolute',
});

const renderCounter = (minCount: number, value: string) => {
  const hasValue = Boolean(value);

  // if value is empty, value is of length 0, or value has met min count, return null
  if (!hasValue || value.length === 0 || value.length >= minCount) {
    return null;
  }

  return (
    <Counter>
      {value.length}/{minCount} characters
    </Counter>
  );
};

const renderStatusMessage = (status: string, statusMessage: string) => (
  <StatusMessage status={status}>{statusMessage}</StatusMessage>
);

const renderStatusIcon = (status: string) => {
  return (
    <div style={{ position: 'absolute', right: '-24px', top: '0' }}>
      <StatusIcon status={status} />
    </div>
  );
};

const TextArea = forwardRef(
  (
    {
      id,
      value,
      inputLabel,
      tooltip,
      labelDescription,
      status,
      statusMessage,
      resize,
      showCounter,
      minCount,
      customInputStyle,
      customStyle,
      isRequired = false,
      customLabelStyle,
      ...rest
    }: ITextAreaProps,
    ref
  ): ReactElement => {
    const darkMode = isDarkMode();

    return (
      <Container style={customStyle}>
        {inputLabel ? (
          <InputLabel
            label={inputLabel}
            tooltip={tooltip}
            labelDescription={labelDescription}
            inputId={`label-${id}`}
            data-test-id={`label-${id}`}
            isRequired={isRequired}
            customStyle={customLabelStyle}
          />
        ) : null}
        <TextAreaContainer className={darkMode ? 'darkmode' : 'lightmode'}>
          <StyledTextArea
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            ref={ref}
            id={id}
            hasError={status === 'error'}
            hasSuccess={status === 'success'}
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            resize={resize}
            style={customInputStyle}
            value={value}
            {...rest}
          />
          {status ? renderStatusIcon(status) : null}
        </TextAreaContainer>
        <MessageContainer className={`status-${id}`}>
          {showCounter && renderCounter(minCount, value)}
          {statusMessage && status !== 'default' ? renderStatusMessage(status, statusMessage) : null}
        </MessageContainer>
      </Container>
    );
  }
);

TextArea.defaultProps = {
  status: 'default',
  resize: 'none',
  showCounter: false,
  minCount: 100,
  rows: 6,
  value: '',
  onChange: () => null,
};

TextArea.propTypes = {
  /** Unique id for the input field */
  id: PropTypes.string,
  /** Textarea value */
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  /** Textarea rows */
  rows: PropTypes.number,
  /** Label of textarea */
  inputLabel: PropTypes.string,
  /** Label description */
  labelDescription: PropTypes.string,
  /** Tooltip next to label */
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  tooltip: PropTypes.shape({
    icon: PropTypes.any,
    text: PropTypes.string,
    overlayStyle: PropTypes.object,
    placement: PropTypes.oneOf(['left', 'right', 'top', 'bottom', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight']),
  }),
  /** Show character counter below textarea */
  showCounter: PropTypes.bool,
  /** Minimum amount of characters required */
  minCount: PropTypes.number,
  /** Determines how border color and status should be rendered*/
  status: PropTypes.oneOf(['default', 'caution', 'error', 'success']),
  /** Status Message */
  statusMessage: PropTypes.any,
  /** Axis textarea can be resized to */
  resize: PropTypes.oneOf(['horizontal', 'vertical', 'both', 'none']),
  /** Custom textarea styles */
  customInputStyle: PropTypes.object,
};

export default TextArea;
